// Centralized fetch wrapper — ported from teliance-chat-portal/src/utils/api.js.
// Injects the Bearer token from localStorage; on 401 does a single-flight
// rotating OAuth refresh against auth.teliance.com/token, then retries the
// original request once.

import { authLogout } from '@/contexts/AuthContext';
import { refreshCentralSession } from '@/lib/authSession.js';

// Dedupe concurrent refreshes so a burst of 401s triggers one token rotation.
let isRefreshing = false;
let refreshPromise = null;

export const refreshAccessToken = async () => {
  const result = await refreshCentralSession({ force: true });
  return result.token;
};

export const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  };
  if (token && !options.skipAuth) headers['Authorization'] = `Bearer ${token}`;

  // credentials:'omit' — the Bearer header is the source of truth; don't let a
  // stale httpOnly cookie override it.
  let response = await fetch(url, { ...options, headers, credentials: 'omit' });

  if (response.status === 401 && !options._isRetry && !options.skipAuth) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshCentralSession({ force: true });
    }
    const refreshResult = await refreshPromise;
    isRefreshing = false;
    refreshPromise = null;

    if (refreshResult.token) {
      return apiFetch(url, {
        ...options,
        _isRetry: true,
        headers: { ...options.headers, Authorization: `Bearer ${refreshResult.token}` },
      });
    }
    if (refreshResult.status === 'invalid') authLogout();
  }
  return response;
};

// Parse a response as JSON without ever surfacing a raw parse error. A proxy
// (Cloudflare/nginx) can replace an origin response with an HTML error page —
// res.json() on that yields "Unexpected token '<'". Returns the parsed body,
// or a clean { error } object built from the HTTP status.
export const parseJsonSafe = async (res) => {
  const text = await res.text();
  try { return JSON.parse(text); }
  catch { return { error: `Server error (HTTP ${res.status})` }; }
};

export const apiGet = async (url, options = {}) => {
  const r = await apiFetch(url, { ...options, method: 'GET' });
  if (!r.ok) throw new Error(`API Error: ${r.status} ${r.statusText}`);
  return r.json();
};
export const apiPost = async (url, data, options = {}) => {
  const r = await apiFetch(url, { ...options, method: 'POST', body: JSON.stringify(data) });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `API Error: ${r.status}`); }
  return r.json();
};
export const apiPut = async (url, data, options = {}) => {
  const r = await apiFetch(url, { ...options, method: 'PUT', body: JSON.stringify(data) });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `API Error: ${r.status}`); }
  return r.json();
};
